import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

// Lifted verbatim from `call-details-sheet.tsx` — the measurements (32px
// `bg-muted` header band, full-width divider, 20px body inset) are the
// product's, not a fresh design. Distinct from `SettingsSection`, which is for
// editing (taller band, description line, Save/Discard footer); this one is for
// reading a record.
export function DetailSection({
  title,
  children,
  className,
  contentClassName,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
  // The body padding defaults to what a `DetailFields` list wants; override for
  // a section whose body is not a field list — the Agents card uses `py-0`.
  contentClassName?: string;
}) {
  return (
    <Card
      className={cn(
        "border-border-subtle gap-0 overflow-hidden py-0 shadow-none",
        className
      )}
    >
      <CardHeader className="bg-muted flex h-8 items-center px-3">
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent
        className={cn(
          "border-border-subtle border-t px-5 pt-2 pb-0",
          contentClassName
        )}
      >
        {children}
      </CardContent>
    </Card>
  );
}

export function DetailFields({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <dl className={cn("divide-border-subtle divide-y", className)}>
      {children}
    </dl>
  );
}

export interface DetailFieldProps {
  label: string;
  children?: React.ReactNode;
  // For machine-readable values only (session/call IDs, hashes) — monospace is
  // reserved for those, and `break-all` keeps a long identifier inside its
  // column instead of widening the row.
  mono?: boolean;
  className?: string;
}

// The split is proportional (`grid-cols-2`) rather than a fixed label rail —
// this card lives in a ~600px sheet pane, where a fixed rail would strand the
// values far from their labels. Non-mono values lay out as a wrapping flex row
// so a value can be "number + badge" without the caller writing the flex.
export function DetailField({
  label,
  children,
  mono,
  className,
}: DetailFieldProps): React.ReactElement {
  const empty =
    children === undefined ||
    children === null ||
    children === false ||
    children === "";

  return (
    <div
      className={cn(
        "grid min-h-10 grid-cols-2 items-start gap-4 py-3",
        className
      )}
    >
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd
        className={cn(
          "text-sm",
          empty ? "text-muted-foreground" : "text-foreground",
          mono ? "font-mono break-all" : "flex flex-wrap items-center gap-2"
        )}
      >
        {empty ? "—" : children}
      </dd>
    </div>
  );
}
